import type {Container} from '../container.ts';
import {FetchRetryer} from '../fetchretryer.ts';
import {isProjectViewEnabled} from '../allspice.ts';
import {escapePathSegments} from '../utils.ts';

export const SCHEMA_TYPES = {
  ALTIUM_SCHEMATIC: 'ALTIUM_SCHEMATIC', // This is purely defined from the frontend
  PCB: 'Pcb',
  PCB_COMPONENT_LIB: 'PcbComponentLib',
  SCHEMATIC: 'Schematic',
  SCHEMATIC_COMPONENT_LIB: 'SchematicComponentLib',
  SINGLE_PAGE_SCHEMATIC: 'SinglePageSchematic',
  UNKNOWN: 'Unknown',
} as const;
export type SchemaType = typeof SCHEMA_TYPES[keyof typeof SCHEMA_TYPES];

export const AllSpiceJsonLoader = {
  /**
   * Builds the URL to the JSON.  If there's no tree path, returns null.
   * @param {Container} container
   * @param {string} repoOwner
   * @param {string} repoName
   * @param {string} commit
   * @param {string} treePath
   * @returns {string | null}
   */
  buildUrl(
    container: Container,
    repoOwner: string,
    repoName: string,
    commit: string,
    treePath: string): string | null {
    /** @type {string} */
    const appSubUrl = container.lookup('appSubUrl');
    const queryParams = new URLSearchParams();
    queryParams.append('type', 'json');
    if (window?.config?.renderVersion) {
      queryParams.append('v', window.config.renderVersion);
    }
    const queryString = queryParams.toString();
    return treePath ? `${appSubUrl}/${repoOwner}/${repoName}/preview/commit/${commit}/${escapePathSegments(treePath)}?${queryString}` : null;
  },

  /**
   * Builds the URL to the project level JSON.  If there's no tree path, returns null.
   * @param {Container} container
   * @param {string} repoOwner
   * @param {string} repoName
   * @param {string} commit
   * @param {string} treePath
   * @returns {string | null}
   */
  buildPrjUrl(
    container: Container,
    repoOwner: string,
    repoName: string,
    commit: string,
    treePath: string): string | null {
    if (!treePath) {
      return null;
    }
    const appSubUrl = container.lookup('appSubUrl');
    const queryParams = new URLSearchParams();
    if (window?.config?.renderVersion) {
      queryParams.append('v', window.config.renderVersion);
    }
    let queryString = queryParams.toString();
    if (queryString.length > 0) {
      queryString = `?${queryString}`;
    }
    return `${appSubUrl}/${repoOwner}/${repoName}/project/commit/${commit}/${escapePathSegments(treePath)}${queryString}`;
  },

  /**
   * Builds the URL to the project level JSON.  If there's no tree path, returns null.
   * @param {Container} container
   * @param {string} repoOwner
   * @param {string} repoName
   * @param {string} commitBase
   * @param {string} commitHead
   * @returns {string | null}
   */
  buildPrjRangeUrl(
    container: Container,
    repoOwner: string,
    repoName: string,
    commitBase: string,
    commitHead: string): string {
    const appSubUrl = container.lookup('appSubUrl');
    const queryParams = new URLSearchParams();
    if (commitBase !== '') {
      queryParams.append('project_view', String(isProjectViewEnabled()));
    }
    if (window?.config?.renderVersion) {
      queryParams.append('v', window.config.renderVersion);
    }
    let queryString = queryParams.toString();
    if (queryString.length > 0) {
      queryString = `?${queryString}`;
    }
    const range = commitBase === '' ? commitHead : `${commitBase}..${commitHead}`;
    return `${appSubUrl}/${repoOwner}/${repoName}/project/compare/${range}${queryString}`;
  },

  /**
   * Standardizes the JSON to handle inconsistencies between file formats.
   * @param {object} json
   * @returns {object}
   */
  standardizeJson(json: Record<string, unknown>) {
    if (json && !json.type) {
      // Altium schematics do not have a type, so we need to infer it by choosing a randomly selected attribute 'html_id'
      if (json.html_id) {
        json.type = SCHEMA_TYPES.ALTIUM_SCHEMATIC;
      } else if (json.pages) {
        json.type = SCHEMA_TYPES.SCHEMATIC;
      } else {
        json.type = SCHEMA_TYPES.UNKNOWN;
      }
    }

    // For multi-page schematis, propagate the type to each page.
    if (json && json.pages && json.type) {
      const pages = json.pages as Record<string, Record<string, unknown>>;
      for (const page of Object.keys(pages)) {
        pages[page].type = json.type;
      }
    }

    return json;
  },

  /**
   * @param {Container} container
   * @param {string | null} headUrl
   * @param {string | null} baseUrl
   * @returns {Promise<{headJson: any, baseJson: any}>} Promise that resolves
   *   when both base and head JSON load successfully
   */
  load<T extends object = Record<string, any>>(container: Container, headUrl: string | null, baseUrl: string | null): Promise<{headJson: T; baseJson: T}> {
    let head = Promise.resolve({});
    let base = Promise.resolve({});

    if (headUrl) {
      const headFetch = FetchRetryer.for(container, headUrl, {});
      // eslint-disable-next-line github/no-then
      head = headFetch.promise.then((headResult: FetchRetryer) => {
        return headResult.response?.clone().json() ?? Promise.reject(new Error('Failed to load head JSON'));
      });
      headFetch.startFetching();
    }

    if (baseUrl) {
      const baseFetch = FetchRetryer.for(container, baseUrl, {});
      // eslint-disable-next-line github/no-then
      base = baseFetch.promise.then((baseResult: FetchRetryer) => {
        return baseResult.response?.clone().json() ?? Promise.reject(new Error('Failed to load base JSON'));
      });
      baseFetch.startFetching();
    }

    // eslint-disable-next-line github/no-then
    return Promise.all([head, base]).then(([headJson, baseJson]) => {
      const standardizedHeadJson: T = this.standardizeJson(headJson) as T;
      const standardizedBaseJson: T = this.standardizeJson(baseJson) as T;
      return {headJson: standardizedHeadJson, baseJson: standardizedBaseJson};
    });
  },
};
